/**
 * IncidentsSettingsView.js
 *
 * Full-screen settings view for Incidents module configuration.
 * WBC3 / MAIB Reporting Requirements
 *
 * Settings:
 * 1. Default Severity - Initial severity for new incidents
 * 2. Investigation Required Threshold - Severity level requiring investigation
 * 3. Auto-close When Actions Complete - Auto-close when all actions done
 * 4. Require Investigation Report - WBC3 compliance for high-severity
 * 5. Show Closed Incidents - Toggle visibility of closed incidents
 * 6. MAIB Notification Reminder - Show reminder for serious incidents
 * 7. Corrective Action Target Days - Target days for completion
 *
 * Pattern: Full-screen overlay accessed via gear icon (same as DrillSettingsView)
 */

import React, { useState } from 'react';
import {
  AlertTriangle,
  Search,
  CheckCircle,
  Bell,
  Eye,
  Clock,
  Info,
  RotateCcw,
  Check,
  Shield,
  FileText
} from 'lucide-react';
import { useIncidentsConfig, SEVERITY_OPTIONS } from '../../../../hooks/domain/incidents/useIncidentsConfig';
// FT-356c: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header raw ArrowLeft back button removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

/**
 * Section wrapper component
 */
const SettingsSection = ({ icon: Icon, title, description, children, regulatory = false }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <div className="bg-gray-900 px-5 py-3">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-cyan-400" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-white">{title}</h3>
            {regulatory && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-amber-500 text-white rounded">
                WBC3
              </span>
            )}
          </div>
          {description && (
            <p className="text-sm text-slate-300 mt-0.5">{description}</p>
          )}
        </div>
      </div>
    </div>
    <div className="p-5">
      {children}
    </div>
  </div>
);

/**
 * Toggle switch component
 */
const Toggle = ({ enabled, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`
      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full
      border-2 border-transparent transition-colors duration-200 ease-in-out
      focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2
      ${enabled ? 'bg-cyan-600' : 'bg-slate-200'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    `}
  >
    <span
      className={`
        pointer-events-none inline-block h-5 w-5 transform rounded-full
        bg-white shadow ring-0 transition duration-200 ease-in-out
        ${enabled ? 'translate-x-5' : 'translate-x-0'}
      `}
    />
  </button>
);

/**
 * Number input with range
 */
const NumberInput = ({ value, onChange, min, max, unit = 'days' }) => {
  const [localValue, setLocalValue] = useState(value);

  const handleBlur = () => {
    const clamped = Math.min(max, Math.max(min, localValue));
    setLocalValue(clamped);
    if (clamped !== value) {
      onChange(clamped);
    }
  };

  // Sync when external value changes
  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <div className="flex items-center gap-3">
      <input
        type="number"
        value={localValue}
        onChange={(e) => setLocalValue(parseInt(e.target.value) || min)}
        onBlur={handleBlur}
        min={min}
        max={max}
        className="w-20 px-3 py-2 text-sm border border-slate-300 rounded-lg
          focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
      />
      <span className="text-sm text-slate-500">{unit}</span>
      <span className="text-xs text-slate-400">({min}-{max})</span>
    </div>
  );
};

/**
 * Select dropdown component
 */
const SelectInput = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="px-3 py-2 text-sm border border-slate-300 rounded-lg
      focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500
      bg-white cursor-pointer"
  >
    {options.map(opt => (
      <option key={opt.value} value={opt.value}>
        {opt.label} - {opt.description}
      </option>
    ))}
  </select>
);

/**
 * Main IncidentsSettingsView component
 */
const IncidentsSettingsView = ({ onBack }) => {
  // FT-356c: register parent's onBack as the shell-level back handler.
  useSetBackHandler(onBack);

  const {
    defaultSeverity,
    investigationRequiredThreshold,
    autoCloseWhenActionsComplete,
    requireInvestigationReport,
    showClosedIncidents,
    maibNotificationReminder,
    correctiveActionTargetDays,
    disabledRegulatorySettings,
    setDefaultSeverity,
    setInvestigationRequiredThreshold,
    setAutoCloseWhenActionsComplete,
    setRequireInvestigationReport,
    setShowClosedIncidents,
    setMaibNotificationReminder,
    setCorrectiveActionTargetDays,
    resetToDefaults,
    lastModified,
    modifiedBy
  } = useIncidentsConfig();

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Show feedback message
  const showFeedback = (message, type = 'success') => {
    setFeedback({ message, type });
    setTimeout(() => setFeedback(null), 3000);
  };

  // Handle reset
  const handleReset = async () => {
    await resetToDefaults();
    setShowResetConfirm(false);
    showFeedback('Settings reset to defaults');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* FT-356c: header raw ArrowLeft back button removed — bottom bar owns Back. */}
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-slate-900">Incidents Settings</h1>
                <p className="text-sm text-slate-500">Configure incident reporting for this vessel</p>
              </div>
            </div>

            <button
              onClick={() => setShowResetConfirm(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600
                hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Feedback Toast */}
      {feedback && (
        <div className={`
          fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg
          ${feedback.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
        `}>
          <div className="flex items-center gap-2">
            {feedback.type === 'success' ? (
              <Check className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            {feedback.message}
          </div>
        </div>
      )}

      {/* Reset Confirmation */}
      {showResetConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Reset to Defaults?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  This will reset all incident settings to their default values, including re-enabling all compliance features. This action is logged.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700"
              >
                Reset Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Audit Notice */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Audit Trail Active</p>
            <p className="mt-0.5">
              All changes are logged with your name and timestamp for SMS compliance.
            </p>
          </div>
        </div>

        {/* Regulatory Warning */}
        {disabledRegulatorySettings?.length > 0 && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Regulatory Settings Disabled</p>
              <p className="mt-0.5">
                You have disabled {disabledRegulatorySettings.length} WBC3/MAIB compliance setting(s): {' '}
                <strong>{disabledRegulatorySettings.join(', ')}</strong>.
                This may affect audit compliance.
              </p>
            </div>
          </div>
        )}

        {/* Section 1: Default Severity */}
        <SettingsSection
          icon={AlertTriangle}
          title="Default Severity"
          description="Initial severity level for new incident reports"
        >
          <div>
            <p className="text-sm text-slate-600 mb-3">
              New incidents will default to this severity level. Can be changed when reporting.
            </p>
            <SelectInput
              value={defaultSeverity}
              onChange={setDefaultSeverity}
              options={SEVERITY_OPTIONS}
            />
          </div>
        </SettingsSection>

        {/* Section 2: Investigation Required Threshold */}
        <SettingsSection
          icon={Search}
          title="Investigation Required Threshold"
          description="Severity level that triggers formal investigation requirement"
        >
          <div>
            <p className="text-sm text-slate-600 mb-3">
              Incidents at or above this severity will require a formal investigation to be completed.
            </p>
            <SelectInput
              value={investigationRequiredThreshold}
              onChange={setInvestigationRequiredThreshold}
              options={SEVERITY_OPTIONS}
            />
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Guidance:</strong> WBC3 recommends investigating all incidents rated Medium or higher.
                Critical incidents should always trigger an investigation.
              </p>
            </div>
          </div>
        </SettingsSection>

        {/* Section 3: Auto-close When Actions Complete */}
        <SettingsSection
          icon={CheckCircle}
          title="Auto-close When Actions Complete"
          description="Automatically close incidents when all corrective actions are completed"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Enable Auto-close</p>
              <p className="text-sm text-slate-500 mt-1">
                When enabled, incidents will automatically be marked as closed once all
                corrective actions have been completed.
              </p>
            </div>
            <Toggle
              enabled={autoCloseWhenActionsComplete}
              onChange={setAutoCloseWhenActionsComplete}
            />
          </div>
        </SettingsSection>

        {/* Section 4: Require Investigation Report */}
        <SettingsSection
          icon={FileText}
          title="Require Investigation Report"
          description="Require formal investigation documentation for high-severity incidents"
          regulatory={true}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Enforce Investigation Report</p>
              <p className="text-sm text-slate-500 mt-1">
                When enabled, incidents meeting the investigation threshold cannot be closed
                without a completed investigation report.
              </p>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Regulatory Note:</strong> WBC3 Section 13 requires investigation of all
                  safety-related incidents. Disabling may affect audit compliance.
                </p>
              </div>
            </div>
            <Toggle
              enabled={requireInvestigationReport}
              onChange={setRequireInvestigationReport}
            />
          </div>
        </SettingsSection>

        {/* Section 5: MAIB Notification Reminder */}
        <SettingsSection
          icon={Bell}
          title="MAIB Notification Reminder"
          description="Show reminder for MAIB-reportable incidents"
          regulatory={true}
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Show MAIB Reporting Reminder</p>
              <p className="text-sm text-slate-500 mt-1">
                When enabled, critical and high-severity incidents will display a reminder
                about MAIB reporting requirements (serious casualties within 24 hours).
              </p>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Regulatory Note:</strong> MAIB requires notification of serious marine
                  casualties within 24 hours. This reminder helps ensure compliance.
                </p>
              </div>
            </div>
            <Toggle
              enabled={maibNotificationReminder}
              onChange={setMaibNotificationReminder}
            />
          </div>
        </SettingsSection>

        {/* Section 6: Corrective Action Target Days */}
        <SettingsSection
          icon={Clock}
          title="Corrective Action Target"
          description="Target days for completing corrective actions"
        >
          <div>
            <p className="text-sm text-slate-600 mb-3">
              Set the target number of days to complete corrective actions. Actions exceeding this will be flagged.
            </p>
            <NumberInput
              value={correctiveActionTargetDays}
              onChange={setCorrectiveActionTargetDays}
              min={7}
              max={365}
              unit="days"
            />
          </div>
        </SettingsSection>

        {/* Section 7: Show Closed Incidents */}
        <SettingsSection
          icon={Eye}
          title="Show Closed Incidents"
          description="Display closed incidents in the main incidents list"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Include Closed Incidents</p>
              <p className="text-sm text-slate-500 mt-1">
                When enabled, closed incidents will appear in the main Active Incidents list.
                Closed incidents are always available in the History tab.
              </p>
            </div>
            <Toggle
              enabled={showClosedIncidents}
              onChange={setShowClosedIncidents}
            />
          </div>
        </SettingsSection>

        {/* Last Modified */}
        {lastModified && (
          <div className="text-xs text-slate-500 text-center pt-4">
            Last modified: {new Date(lastModified).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
            {modifiedBy?.name && ` by ${modifiedBy.name}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default IncidentsSettingsView;
